import type { User } from '@features/auth/types';
import Avatar, { type AvatarProps } from '@mui/material/Avatar';
import type { SxProps, Theme } from '@mui/material/styles';
import { computeAvatarInitials } from '@shared/utils/initials';

export interface UserAvatarProps extends Omit<AvatarProps, 'children' | 'alt' | 'src' | 'srcSet'> {
  user: Pick<User, 'name' | 'firstName' | 'lastName' | 'email'> | null | undefined;
}

/**
 * Avatar showing up to two initials derived from the user's name — never the
 * raw display name (avoids short names like "You" rendering as "YOU" via CSS).
 */
export function UserAvatar({ user, sx, className, ...props }: UserAvatarProps) {
  const letters = computeAvatarInitials(user);
  const label = user?.name.trim() ?? user?.email ?? undefined;
  const sxArray: readonly SxProps<Theme>[] = sx ? (Array.isArray(sx) ? sx : [sx]) : [];

  return (
    <Avatar
      aria-label={label}
      className={className}
      {...props}
      sx={
        [
          {
            textTransform: 'none',
            letterSpacing: 0,
            lineHeight: 1,
            fontWeight: 600,
          },
          ...sxArray,
        ] as SxProps<Theme>
      }
    >
      {letters}
    </Avatar>
  );
}
